// Clockora integrated module — the full attendance app embedded in the
// NewAdminDashboard. Everything renders inside .clockora-scope so the
// design system stays isolated from the host dashboard.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, Clock3, LayoutDashboard, ScrollText, Users } from "lucide-react";
import clsx from "clsx";
import { motion, AnimatePresence } from "framer-motion";
import { ClockoraApiProvider } from "./api";
import { TimeSyncProvider } from "./ui";
import useAdminTheme from "../admindashboard/useTheme.js";
import "./clockora.css";

const DashboardClient = React.lazy(() => import("./DashboardClient").then((m) => ({ default: m.DashboardClient })));
const KioskClient = React.lazy(() => import("./KioskClient").then((m) => ({ default: m.KioskClient })));
const LogsClient = React.lazy(() => import("./LogsClient").then((m) => ({ default: m.LogsClient })));
const EmployeesClient = React.lazy(() => import("./EmployeesClient").then((m) => ({ default: m.EmployeesClient })));
const SchedulesClient = React.lazy(() => import("./SchedulesClient").then((m) => ({ default: m.SchedulesClient })));

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "kiosk", label: "Kiosk", icon: Clock3 },
  { id: "logs", label: "Time Logs", icon: ScrollText },
  { id: "employees", label: "Employees", icon: Users },
  { id: "schedules", label: "Schedules", icon: CalendarClock },
];

const STATE_KEY = "clockora.tab.v1";

function Logo({ size = 38 }) {
  return (
    <span className="flex items-center gap-3">
      <span
        className="grid flex-none place-items-center rounded-[12px]"
        style={{
          width: size,
          height: size,
          background: "linear-gradient(160deg, #ffe14d, #f2b705)",
          boxShadow: "0 8px 22px -8px rgba(255, 200, 0, 0.6)",
        }}
      >
        <Clock3 size={size * 0.55} strokeWidth={2.5} color="#181202" />
      </span>
      <span className="font-display text-[19px] font-bold tracking-tight text-[var(--ink)]">
        Clock<span style={{ color: "rgb(var(--c-yellow))" }}>ora</span>
      </span>
    </span>
  );
}

function ClockoraIntroCurtain({ show, isLight }) {
  const accent = "#ffd02e";
  const accentSoft = isLight ? "rgba(217, 154, 0, 0.86)" : "rgba(255, 208, 46, 0.9)";
  const primary = isLight ? "#2563eb" : "#3f7dff";
  const pageBg = isLight ? "#eef2fc" : "#081229";
  const pageBgGradient = isLight
    ? "radial-gradient(1200px 800px at 12% -10%, rgba(199,219,255,0.95) 0%, rgba(238,242,252,0) 55%), radial-gradient(1100px 700px at 100% 120%, rgba(255,235,159,0.92) 0%, rgba(238,242,252,0) 55%), linear-gradient(135deg,#e4ebff 0%, #eef2fc 45%, #fff8e0 100%)"
    : "radial-gradient(1200px 800px at 12% -10%, rgba(63,125,255,0.35) 0%, rgba(8,18,41,0) 55%), radial-gradient(1100px 700px at 100% 120%, rgba(255,201,39,0.28) 0%, rgba(8,18,41,0) 55%), radial-gradient(900px 600px at 50% 120%, rgba(63,125,255,0.22) 0%, rgba(8,18,41,0) 60%)";
  const titleClass = isLight
    ? "mt-6 font-display text-3xl font-semibold tracking-tight text-slate-900"
    : "mt-6 font-display text-3xl font-semibold tracking-tight text-white";
  const captionClass = isLight
    ? "mt-3 font-display text-xs text-slate-500 italic"
    : "mt-3 font-display text-xs italic";
  const dividerBg = isLight ? "rgba(15,23,42,0.12)" : "rgba(255,255,255,0.08)";
  const tileCls = isLight
    ? "relative grid size-20 place-items-center rounded-[28px] p-2.5 border bg-white"
    : "relative grid size-20 place-items-center rounded-[28px] p-2.5 border border-white/10";
  const tileStyle = isLight
    ? {
        borderColor: "rgba(15,23,42,0.08)",
        background: "linear-gradient(160deg, #ffe14d, #f2b705)",
        boxShadow: "0 2px 4px rgba(15,23,42,0.08), 0 24px 48px -20px rgba(217,154,0,0.45)",
      }
    : {
        background: "linear-gradient(160deg, #ffe14d, #f2b705)",
        boxShadow:
          "0 24px 60px -18px rgba(250,204,21,0.35), 0 0 0 1px rgba(250,204,21,0.15) inset",
      };
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="clockora-intro-curtain"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.06, filter: "blur(10px)" }}
          transition={{ duration: 0.75, ease: [0.65, 0, 0.35, 1] }}
          className="absolute inset-0 z-[140] grid place-items-center"
          style={{
            background: pageBgGradient,
            backgroundColor: pageBg,
            borderRadius: "var(--card-radius, 20px)",
          }}
        >
          <div className="flex flex-col items-center">
            <motion.span
              initial={{ opacity: 0, scale: 0.6, rotate: -22 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 150, damping: 13, delay: 0.05 }}
              className={tileCls}
              style={tileStyle}
            >
              <motion.span
                className="absolute inset-0 rounded-[28px] border-2"
                style={{ borderColor: accent }}
                initial={{ opacity: 0.9, scale: 1 }}
                animate={{ opacity: 0, scale: 1.55 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
              />
              <Clock3 size={38} strokeWidth={2.5} color="#181202" />
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className={titleClass}
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            >
              iSource Clockora
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
              className="mt-2 text-[10px] font-bold tracking-[0.34em] uppercase"
              style={{ color: accentSoft }}
            >
              Time &amp; Attendance Hub
            </motion.p>

            <div className="relative mt-8 h-px w-56 overflow-hidden" style={{ background: dividerBg }}>
              <motion.span
                className="absolute inset-y-0 left-0"
                style={{ background: accent }}
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className={captionClass}
              style={{ color: isLight ? "rgba(15,23,42,0.55)" : accentSoft }}
            >
              Loading attendance dashboard…
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function ClockoraApp({ axiosInstance }) {
  const { isLight } = useAdminTheme();
  const [tab, setTab] = useState(() => {
    try {
      const t = window.sessionStorage.getItem(STATE_KEY);
      return NAV.some((n) => n.id === t) ? t : "overview";
    } catch {
      return "overview";
    }
  });
  const [minElapsed, setMinElapsed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), 1400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(STATE_KEY, tab);
    } catch {
      /* ignore */
    }
  }, [tab]);

  // Permanent cross-module guard: if any leaflet-owning tab raced its teardown with
  // Clockora's splash mount (leaving a pending zoom transition end that calls
  // L.DomUtil.getPosition on a detached pane → parentElement undefined → crash), we
  // monkey-patch getPosition for the lifetime of this module to return Point(0,0)
  // (safe: leaflet zoom end on detached map only animates display; next valid map
  // mount restores pane positions via map.setView / invalidateSize anyway).
  useEffect(function leafletGetPositionGuard() {
    if (typeof window === "undefined") return;
    let restored = false;
    let orig = null;
    try {
      if (window.L && window.L.DomUtil && typeof window.L.DomUtil.getPosition === "function") {
        orig = window.L.DomUtil.getPosition;
        window.L.DomUtil.getPosition = function (el) {
          if (restored) return orig.call(window.L.DomUtil, el);
          try {
            return orig.call(window.L.DomUtil, el);
          } catch (_) {
            return new window.L.Point(0, 0);
          }
        };
      }
    } catch (_) {
      orig = null;
    }
    return () => {
      if (orig && window.L && window.L.DomUtil) {
        restored = true;
        window.L.DomUtil.getPosition = orig;
      }
    };
  }, []);

  const navigate = useCallback((id) => {
    if (NAV.some((n) => n.id === id)) setTab(id);
  }, []);

  const content = useMemo(() => {
    switch (tab) {
      case "kiosk":
        return <KioskClient />;
      case "logs":
        return <LogsClient />;
      case "employees":
        return <EmployeesClient />;
      case "schedules":
        return <SchedulesClient />;
      default:
        return <DashboardClient onNavigate={navigate} />;
    }
  }, [tab, navigate]);

  return (
    <div
      className={clsx("clockora-scope relative w-full overflow-hidden")}
      style={{
        minHeight: "calc(100vh - 180px)",
        height: "calc(100vh - 180px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ClockoraApiProvider axiosInstance={axiosInstance}>
        <TimeSyncProvider axiosInstance={axiosInstance}>
          <ClockoraIntroCurtain show={!minElapsed} isLight={isLight} />
          {/* aurora backdrop (absolute, not fixed — stays inside the page) */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(900px 480px at 8% -8%, rgb(var(--c-blue) / 0.16), transparent 60%)," +
                  "radial-gradient(700px 420px at 96% 4%, rgb(var(--c-yellow) / 0.09), transparent 55%)," +
                  "radial-gradient(1000px 600px at 55% 112%, rgb(var(--c-blue) / 0.13), transparent 60%)",
              }}
            />
          </div>

          <div className="relative z-10 flex flex-1 flex-col gap-5 min-h-0">
            {/* header band: logo + tabs (theme is driven by New Admin Dashboard header button) */}
            <div className="card fade-up flex flex-wrap items-center gap-3 p-3 shrink-0">
              <div className="pl-2 pr-1">
                <Logo />
              </div>
              <div className="h-8 w-px" style={{ background: "var(--line)" }} />
              <nav className="flex flex-1 flex-wrap gap-1">
                {NAV.map(({ id, label, icon: Icon }) => {
                  const active = tab === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setTab(id)}
                      className={clsx("nav-item", active && "active")}
                      style={{ width: "auto", padding: "9px 14px" }}
                    >
                      <Icon size={16} strokeWidth={2.2} style={active ? { color: "rgb(var(--c-yellow))" } : undefined} />
                      {label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* page body — flex-1 + min-h:0 lets child pages consume the remaining
                height budget (after header + tab + gap) for their own overflow-scroll
                containers WITHOUT pushing the NewAdminDashboard footer off-screen. */}
            <div className="flex flex-1 flex-col min-h-0">
              <React.Suspense
                fallback={
                  <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 xl:grid-cols-5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="skeleton h-[138px]" />
                    ))}
                  </div>
                }
              >
                {content}
              </React.Suspense>
            </div>
          </div>
        </TimeSyncProvider>
      </ClockoraApiProvider>
    </div>
  );
}
